import { Box, Button, Typography } from "@outschool/backpack";
import {
  ActivityEnrollQueryQuery,
  EnrollmentMeetingPurchaseQueryQuery
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { Loading } from "@outschool/ui-legacy-component-library";
import { capitalize } from "lodash";
import React from "react";

import * as Env from "../../../shared/Env";
import {
  useCreateSetupIntentWithPaymentElement,
  usePaymentMethods
} from "../../hooks/CurrentUser";
import ErrorMessage from "../ErrorMessage";
import { StripeProvider, useDefaultStripeErrorMessage } from "./Stripe";
import StripeAuthorization, {
  BulkPurchaseRecurringPaymentTypes
} from "./StripeAuthorization";
import StripeNewCard from "./StripeNewCard";

interface StripeRecurringSavedPaymentFormProps {
  activity?:
    | NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["activity"]
    | NonNullable<
        EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]
      >["activity"];
  isWeeklyPayment: boolean;
  bulkPurchaseRecurringPaymentMethod?: BulkPurchaseRecurringPaymentTypes;
  handleStripePaymentAccepted: () => Promise<void>;
  handleCheckoutFormCancel: () => void;
}

export default function StripeRecurringSavedPaymentForm(
  props: StripeRecurringSavedPaymentFormProps
) {
  return (
    <StripeProvider amountInCents={0}>
      <RecurringSavedPaymentForm {...props} />
    </StripeProvider>
  );
}

function RecurringSavedPaymentForm({
  activity,
  isWeeklyPayment,
  bulkPurchaseRecurringPaymentMethod,
  handleStripePaymentAccepted,
  handleCheckoutFormCancel
}: StripeRecurringSavedPaymentFormProps) {
  const { t } = useTranslation(
    "client\\components\\payments\\StripeRecurringSavedPaymentForm"
  );

  const {
    loading: paymentMethodsLoading,
    error: paymentMethodsError,
    paymentMethods
  } = usePaymentMethods();
  const [defaultPaymentMethod] = paymentMethods;
  const errorMessage = useDefaultStripeErrorMessage();
  const [isAuthorizationChecked, setIsAuthorizationChecked] =
    React.useState(false);

  const [isFormComplete, setIsFormComplete] = React.useState(false);
  const [
    createSetupIntentWithPaymentElement,
    { error: setupIntentError, loading: setupIntentLoading }
  ] = useCreateSetupIntentWithPaymentElement(defaultPaymentMethod?.uid);

  const handleSubmit = React.useCallback(async () => {
    await createSetupIntentWithPaymentElement();
    await handleStripePaymentAccepted();
  }, [createSetupIntentWithPaymentElement, handleStripePaymentAccepted]);

  if (paymentMethodsLoading) {
    return <Loading />;
  }

  const chargeText = defaultPaymentMethod
    ? t`You won’t be charged today, but your saved card will be used for future recurring payments.`
    : t`You won’t be charged today, but we need to save a card for future recurring payments.`;
  const withdrawalText = isWeeklyPayment
    ? t`You can withdraw your enrollment at any time.`
    : t`You can stop your subscription at any time.`;

  return (
    <Box>
      <Typography variant="h3" sx={{ marginBottom: "0.5em" }}>
        {defaultPaymentMethod ? t`Saved card` : t`Add a card`}
      </Typography>
      <Typography>
        {chargeText} {withdrawalText}
      </Typography>
      {paymentMethodsError ? (
        <ErrorMessage
          value={errorMessage}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
      ) : defaultPaymentMethod ? (
        <Box
          flex
          sx={{
            justifyContent: "space-between",
            paddingY: "0.5em",
            marginY: "1em",
            borderTop: "1px solid #E3E3E3",
            borderBottom: "1px solid #E3E3E3"
          }}
        >
          {capitalize(defaultPaymentMethod.brand)} ****{" "}
          {defaultPaymentMethod.last4} {t("Exp.")}{" "}
          {/* eslint-disable-next-line i18next/no-literal-string */}
          {defaultPaymentMethod.expirationMonth.toFixed().padStart(2, "0")}/
          {defaultPaymentMethod.expirationYear.toString().substring(2)}
        </Box>
      ) : (
        <Box sx={{ marginTop: "1em" }}>
          <StripeNewCard
            setIsCardComplete={setIsFormComplete}
            setBillingDetails={() => {}}
          />
        </Box>
      )}
      <StripeAuthorization
        setIsSaveForLater={setIsAuthorizationChecked}
        isSaveForLater={isAuthorizationChecked}
        isPaymentProcessing={setupIntentLoading}
        // @ts-ignore TS(2322): Type 'undefined' is not assignable to type 'boolea... Remove this comment to see the full error message
        isPaymentSuccessful={undefined}
        activity={activity}
        isWeeklyPayment={isWeeklyPayment}
        bulkPurchaseRecurringPaymentMethod={bulkPurchaseRecurringPaymentMethod}
      />
      <Button
        variant="link"
        onClick={handleCheckoutFormCancel}
        sx={{
          width: "100%",
          marginBottom: "1em"
        }}
        disabled={setupIntentLoading}
      >
        {t("Go Back")}
      </Button>
      <ErrorMessage value={setupIntentError && setupIntentError.message} />
      <Button
        variant="contained"
        disabled={
          (!isFormComplete && !defaultPaymentMethod) ||
          !isAuthorizationChecked ||
          paymentMethodsLoading ||
          setupIntentLoading
        }
        onClick={handleSubmit}
        sx={{
          width: "100%"
        }}
      >
        {defaultPaymentMethod ? t`Continue` : t`Save card`}
      </Button>
    </Box>
  );
}
